// SUB-OFFICE 7 — THE VERTICAL CIRCULATION CORE (zone: vertical_core).
// Build increment M-B / graphics Increment 8. The lift lobby off Intake where
// the Bureau's favorite machines wait: a working elevator whose buttons IT
// chooses (the cab is a diegetic mask, see cab.ts), a kit-of-parts stair shaft
// down to a locked landing, a permanently STOPPED escalator, the still-locked
// Mezzanine door (Form 6-M, forever pending) beside the lift that works, and
// vertical glass tube risers where the Machine's mail overtakes you. Also here:
// buildDescentStub — the cheap dead-end landing the cab round-trips to, the M-D
// placeholder that proves the Q1 phosphor->bone fade primitive.
//
// Canon coords (spec-map-v2 zone_graph.vertical_core): the lobby is x[30,42]
// z[-17,3]; its west wall (x=30) is shared with Intake's east wall, and the
// cab doors sit at z=-6 directly opposite the interior working-lift door. All
// signage is verbatim from content/machine-floor.json verticalCore.
//
// The lobby is a streamed sub-scene built in world space east of Intake; while
// it is up the resident interior is hidden (ZoneManager visibility-swap). It
// batches to ~14 draw calls / well under 8k tris (budget <=20 / <=8k; task
// ceiling <=35 / <=18k).

import {
  AdditiveBlending, BoxGeometry, BufferAttribute, BufferGeometry,
  CanvasTexture, Color, CylinderGeometry, DoubleSide, Group, Matrix4, Mesh,
  MeshBasicMaterial, MeshLambertMaterial, PlaneGeometry, type Material,
} from 'three';
import { PAL, css, shade } from '../../palette';
import { patchMaterial } from '../../render/scene';
import { Chyron, drawDotText, dotTextWidth, mkCanvas, toTexture } from '../../render/textures';
import type { CollisionBounds } from '../collision';
import type { ZoneManifest, ZoneSpawn, ZoneHotspot, WallSeg, SolidRect, ZoneRect } from './types';
import machineFloor from '../../../content/machine-floor.json';

// --- lobby local frame (world space, east of Intake x=30) -------------------
const W = 30;   // west wall (shared with Intake east wall)
const E = 42;   // east wall
const N = -17;  // north wall
const S = 3;    // south wall
const WALL_H = 4.2;
const CAB_Z = -6;      // cab doors, opposite the interior lift door
const CAB_W = 2.2;     // canon cab 2.2x2.2

/** hex -> linear-space THREE.Color (materials expect linear). */
const colr = (hex: number): Color => new Color(hex).convertSRGBToLinear();

// ============================================================================
// GeoBatch — merge transformed geometries into one non-indexed soup so a whole
// material's worth of props is a single draw call (same idea as exterior.ts).
// ============================================================================

interface UvRegion { u0: number; v0: number; u1: number; v1: number }
const FULL_UV: UvRegion = { u0: 0, v0: 0, u1: 1, v1: 1 };

class GeoBatch {
  private pos: number[] = [];
  private nor: number[] = [];
  private uv: number[] = [];

  add(geo: BufferGeometry, m: Matrix4, region: UvRegion = FULL_UV): void {
    const g = geo.index ? geo.toNonIndexed() : geo;
    g.applyMatrix4(m);
    const p = g.attributes.position.array;
    const n = g.attributes.normal ? g.attributes.normal.array : null;
    const u = g.attributes.uv ? g.attributes.uv.array : null;
    const count = g.attributes.position.count;
    for (let i = 0; i < count; i++) {
      this.pos.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);
      if (n) this.nor.push(n[i * 3], n[i * 3 + 1], n[i * 3 + 2]);
      else this.nor.push(0, 1, 0);
      const su = u ? u[i * 2] : 0;
      const sv = u ? u[i * 2 + 1] : 0;
      this.uv.push(region.u0 + su * (region.u1 - region.u0), region.v0 + sv * (region.v1 - region.v0));
    }
    if (g !== geo) g.dispose();
    geo.dispose();
  }

  get empty(): boolean { return this.pos.length === 0; }
  get tris(): number { return this.pos.length / 9; }

  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('uv', new BufferAttribute(new Float32Array(this.uv), 2));
    return g;
  }
}

const M = (x: number, y: number, z: number, sx = 1, sy = 1, sz = 1): Matrix4 =>
  new Matrix4().makeScale(sx, sy, sz).setPosition(x, y, z);

// ============================================================================
// Procedural signage (verbatim lines on carbon plates; nearest-filtered dots)
// ============================================================================

function makeSignAtlas(lines: string[]): { texture: CanvasTexture; region: (i: number) => UvRegion } {
  const cols = 2;
  const rows = Math.max(1, Math.ceil(lines.length / cols));
  const CW = 512; const CH = 256;
  const [c, ctx] = mkCanvas(CW * cols, CH * rows);
  const wrap = (text: string, maxW: number, dot: number): string[] => {
    const words = text.split(' ');
    const out: string[] = []; let cur = '';
    for (const w of words) {
      const trial = cur ? cur + ' ' + w : w;
      if (dotTextWidth(trial, { dot }) > maxW && cur) { out.push(cur); cur = w; }
      else cur = trial;
    }
    if (cur) out.push(cur);
    return out;
  };
  for (let i = 0; i < lines.length; i++) {
    const col = i % cols; const row = (i / cols) | 0;
    const ox = col * CW; const oy = row * CH;
    ctx.fillStyle = css(shade(PAL.carbon, 0.04)); ctx.fillRect(ox + 10, oy + 10, CW - 20, CH - 20);
    ctx.strokeStyle = 'rgba(233,228,216,0.26)'; ctx.lineWidth = 3;
    ctx.strokeRect(ox + 16, oy + 16, CW - 32, CH - 32);
    const dot = 5;
    const wrapped = wrap(lines[i], CW - 80, dot);
    const lineH = 7 * dot + 10;
    let ty = oy + CH / 2 - (wrapped.length - 1) * lineH / 2 - 14;
    for (const wln of wrapped) {
      const w = dotTextWidth(wln, { dot });
      drawDotText(ctx, wln, ox + (CW - w) / 2, ty, css(PAL.bone), { dot });
      ty += lineH;
    }
  }
  const t = toTexture(c, { nearest: true });
  const region = (i: number): UvRegion => {
    const col = i % cols; const row = (i / cols) | 0;
    return {
      u0: col / cols, u1: (col + 1) / cols,
      v0: 1 - (row + 1) / rows, v1: 1 - row / rows,
    };
  };
  return { texture: t, region };
}

/** A single stencil plate (one line, drawn once). */
function makeStencil(line: string, color: number = PAL.bone): CanvasTexture {
  const [c, ctx] = mkCanvas(512, 128);
  const dot = 5;
  const w = dotTextWidth(line, { dot });
  const scale = Math.min(1, (512 - 40) / Math.max(1, w));
  drawDotText(ctx, line, (512 - w * scale) / 2, 44, css(color), { dot: dot * scale });
  return toTexture(c, { nearest: true });
}

// ============================================================================
// The lift-lobby build
// ============================================================================

export interface VerticalCoreBuild {
  group: Group;
  manifest: ZoneManifest;
  hotspots: ZoneHotspot[];
  chyron: Chyron;
  /** the ambient tube canister overtaking the petitioner (drives on update). */
  tickRiser: (dt: number) => void;
  /** the outside split-flap indicator; controller flaps it during a ride. */
  setFloorFlap: (label: string) => void;
  tris: number;
  buildMs: number;
  dispose(): void;
}

export function buildVerticalCore(reducedMotion: boolean): VerticalCoreBuild {
  const t0 = performance.now();
  const content = machineFloor.verticalCore;
  const group = new Group();
  const disposables: { dispose(): void }[] = [];
  const track = <T extends Material | CanvasTexture>(x: T): T => { disposables.push(x); return x; };

  // --- materials -------------------------------------------------------------
  const boneMat = track(new MeshLambertMaterial({ color: colr(PAL.bone), flatShading: true }));
  patchMaterial(boneMat);
  const carbonMat = track(new MeshLambertMaterial({ color: colr(PAL.carbon), flatShading: true }));
  patchMaterial(carbonMat);
  const brassMat = track(new MeshLambertMaterial({ color: colr(PAL.brass), flatShading: true }));
  patchMaterial(brassMat);
  const amberSolid = track(new MeshBasicMaterial({ color: colr(PAL.amber), fog: false, side: DoubleSide }));
  const phosphorGlow = track(new MeshBasicMaterial({ color: colr(PAL.phosphor), transparent: true, opacity: 0.16, blending: AdditiveBlending, depthWrite: false, fog: false, side: DoubleSide }));
  const glassMat = track(new MeshBasicMaterial({ color: colr(PAL.phosphor), transparent: true, opacity: 0.1, depthWrite: false, side: DoubleSide }));
  const canisterMat = track(new MeshBasicMaterial({ color: colr(PAL.amber), fog: false }));
  const gasMat = track(new MeshBasicMaterial({ color: colr(PAL.gas), fog: false }));

  // --- structure batch: floor, ceiling, walls (bone) -------------------------
  const bone = new GeoBatch();
  bone.add(new BoxGeometry(E - W, 0.1, S - N), M((W + E) / 2, -0.05, (N + S) / 2)); // floor slab
  bone.add(new BoxGeometry(E - W, 0.1, S - N), M((W + E) / 2, WALL_H, (N + S) / 2)); // ceiling
  // perimeter walls (west wall carries the cab opening; keep it whole — the cab
  // doors sit proud of it and the interior lift door is the real threshold)
  bone.add(new BoxGeometry(0.2, WALL_H, S - N), M(W, WALL_H / 2, (N + S) / 2)); // west
  bone.add(new BoxGeometry(0.2, WALL_H, S - N), M(E, WALL_H / 2, (N + S) / 2)); // east
  bone.add(new BoxGeometry(E - W, WALL_H, 0.2), M((W + E) / 2, WALL_H / 2, N)); // north
  bone.add(new BoxGeometry(E - W, WALL_H, 0.2), M((W + E) / 2, WALL_H / 2, S)); // south (low visual ok)
  // cab door recess wainscot (bone), two closed leaves proud of the west wall
  bone.add(new BoxGeometry(0.14, 2.6, CAB_W + 0.6), M(W + 0.18, 1.3, CAB_Z)); // lintel-jamb block
  const leaf = new GeoBatch();
  for (const s of [-1, 1]) leaf.add(new BoxGeometry(0.1, 2.4, CAB_W / 2 - 0.02), M(W + 0.3, 1.2, CAB_Z + s * CAB_W / 4));
  const boneMesh = new Mesh(bone.build(), boneMat); boneMesh.matrixAutoUpdate = false; group.add(boneMesh);
  const leafMesh = new Mesh(leaf.build(), track(new MeshLambertMaterial({ color: colr(shade(PAL.bone, -0.12)), flatShading: true })));
  patchMaterial(leafMesh.material as Material);
  leafMesh.matrixAutoUpdate = false; group.add(leafMesh);

  // --- carbon batch: cab surround, escalator comb frame, mezz door leaf ------
  const carbon = new GeoBatch();
  // cab surround frame proud of the west wall
  carbon.add(new BoxGeometry(0.2, 0.16, CAB_W + 0.8), M(W + 0.4, 2.55, CAB_Z)); // header
  for (const s of [-1, 1]) carbon.add(new BoxGeometry(0.2, 2.6, 0.16), M(W + 0.4, 1.3, CAB_Z + s * (CAB_W / 2 + 0.35)));
  // mezzanine locked door (north wall, beside the cab) — the forever-pending stair
  const MEZ_X = 35;
  carbon.add(new BoxGeometry(1.1, 2.2, 0.12), M(MEZ_X, 1.1, N + 0.16));
  // dead escalator comb frame + static steps (a whole unit, STOPPED) near east
  const escSteps = new GeoBatch();
  const ESC_X = 40.5;
  for (let i = 0; i < 14; i++) {
    const ez = -1 - i * 0.28;
    const ey = 0.1 + i * 0.17;
    escSteps.add(new BoxGeometry(1.6, 0.14, 0.28), M(ESC_X, ey, ez));
  }
  carbon.add(new BoxGeometry(1.9, 0.3, 0.5), M(ESC_X, 0.15, -0.7)); // bottom comb plate (dark)
  const carbonMesh = new Mesh(carbon.build(), carbonMat); carbonMesh.matrixAutoUpdate = false; group.add(carbonMesh);
  const escMesh = new Mesh(escSteps.build(), track(new MeshLambertMaterial({ color: colr(shade(PAL.bone, -0.2)), flatShading: true })));
  patchMaterial(escMesh.material as Material);
  escMesh.matrixAutoUpdate = false; group.add(escMesh);

  // --- brass batch: stair steps kit-of-parts + handrail + call plates --------
  const brass = new GeoBatch();
  const stairSteps = new GeoBatch();
  // the stair shaft: a flight descending toward a locked landing (dressed; the
  // flat capsule cannot yet climb it — gated at the foot, M-D adds StairRun Y)
  const ST_X = 37.5;
  for (let i = 0; i < 12; i++) {
    const sz = -2 + i * 0.28; // canon run 0.28
    const sy = 0.1 - i * 0.17 * 0.4; // shallow visual descent (no Y locomotion)
    stairSteps.add(new BoxGeometry(2.0, 0.14, 0.28), M(ST_X, Math.max(-1.4, sy), sz));
  }
  // handrail: brass cylinder on carbon posts every 4 steps
  for (const s of [-1, 1]) {
    for (let i = 0; i <= 12; i += 4) {
      const sz = -2 + i * 0.28;
      brass.add(new CylinderGeometry(0.05, 0.05, 1.0, 6), M(ST_X + s * 1.0, 0.5, sz));
    }
    brass.add(new CylinderGeometry(0.04, 0.04, 3.6, 6, 1, false).rotateX(Math.PI / 2), M(ST_X + s * 1.0, 1.0, -0.3));
  }
  // two call plates flanking the cab — the working plate (amber-lit) and the
  // dead plate (the Bureau installed two; only one answers)
  for (const s of [-1, 1]) brass.add(new BoxGeometry(0.1, 0.5, 0.32), M(W + 0.42, 1.2, CAB_Z + s * 1.6));
  const brassMesh = new Mesh(brass.build(), brassMat); brassMesh.matrixAutoUpdate = false; group.add(brassMesh);
  const stairMesh = new Mesh(stairSteps.build(), boneMat); stairMesh.matrixAutoUpdate = false; group.add(stairMesh);

  // --- emissive pilots: one amber pilot per call plate (working lit) ---------
  const pilots = new GeoBatch();
  pilots.add(new PlaneGeometry(0.08, 0.08), M(W + 0.48, 1.35, CAB_Z - 1.6)); // working plate pilot
  const pilotMesh = new Mesh(pilots.build(), amberSolid); pilotMesh.matrixAutoUpdate = false; group.add(pilotMesh);
  // gas ceiling panel (the cold institutional light source)
  const panel = new Mesh(new PlaneGeometry(3.0, 3.0), gasMat);
  panel.position.set(35, WALL_H - 0.06, -6); panel.rotation.x = Math.PI / 2;
  panel.matrixAutoUpdate = false; panel.updateMatrix(); group.add(panel);

  // --- vertical tube risers (glass) + the overtaking canister ----------------
  // the Machine's mail runs vertically beside the shaft and beats you down.
  const risers = new GeoBatch();
  const RIS_X = 31.0; const RIS_Z = -8.4;
  for (const dz of [-0.5, 0.5]) {
    risers.add(new CylinderGeometry(0.18, 0.18, WALL_H, 10, 1, true), M(RIS_X, WALL_H / 2, RIS_Z + dz));
  }
  const riserMesh = new Mesh(risers.build(), glassMat); riserMesh.matrixAutoUpdate = false; group.add(riserMesh);
  const phosphorSeam = new Mesh(new PlaneGeometry(0.5, WALL_H), phosphorGlow);
  phosphorSeam.position.set(RIS_X + 0.001, WALL_H / 2, RIS_Z); phosphorSeam.rotation.y = Math.PI / 2;
  phosphorSeam.matrixAutoUpdate = false; phosphorSeam.updateMatrix(); group.add(phosphorSeam);
  const canister = new Mesh(new CylinderGeometry(0.1, 0.1, 0.4, 8), canisterMat);
  canister.position.set(RIS_X, WALL_H, RIS_Z - 0.5); group.add(canister);
  let canisterY = WALL_H;
  const tickRiser = (dt: number): void => {
    if (reducedMotion) { canister.visible = false; return; }
    canisterY -= dt * 3.4; // faster than any descent: it wins every time
    if (canisterY < -0.4) canisterY = WALL_H + Math.random() * 2;
    canister.position.y = canisterY;
  };

  // --- signage: verbatim lines on carbon plates ------------------------------
  const signLines = [
    content.elevatorLines[0], content.elevatorLines[1],
    content.stairLines[0], content.escalatorLines[0],
  ];
  const atlas = makeSignAtlas(signLines);
  const signMat = track(new MeshLambertMaterial({ map: track(atlas.texture), transparent: true, side: DoubleSide }));
  patchMaterial(signMat);
  const signs = new GeoBatch();
  const signSpots: [number, number, number, number][] = [
    [W + 0.35, 2.9, CAB_Z, Math.PI / 2],    // over the cab (elevatorLines[0])
    [W + 0.35, 2.2, CAB_Z + 2.4, Math.PI / 2], // beside the cab (elevatorLines[1])
    [ST_X, 2.4, S - 0.2, Math.PI],          // stair head (stairLines[0])
    [ESC_X, 2.6, S - 0.2, Math.PI],         // escalator (escalatorLines[0])
  ];
  for (let i = 0; i < signSpots.length; i++) {
    const [x, y, z, yaw] = signSpots[i];
    signs.add(new PlaneGeometry(1.9, 0.95), new Matrix4().makeRotationY(yaw).premultiply(M(x, y, z)), atlas.region(i));
  }
  const signMesh = new Mesh(signs.build(), signMat); signMesh.matrixAutoUpdate = false; group.add(signMesh);

  // --- stencils: escalator OUT OF SERVICE + mezzanine PENDING ----------------
  const escStencilMat = track(new MeshBasicMaterial({ map: track(makeStencil('OUT OF SERVICE — SERVICE WAS NOT DEFINED', PAL.amber)), transparent: true, side: DoubleSide, fog: false }));
  const escStencil = new Mesh(new PlaneGeometry(1.7, 0.42), escStencilMat);
  escStencil.position.set(ESC_X, 1.4, -0.42); escStencil.rotation.y = Math.PI;
  escStencil.matrixAutoUpdate = false; escStencil.updateMatrix(); group.add(escStencil);
  const mezStencilMat = track(new MeshBasicMaterial({ map: track(makeStencil('STAIRWELL PENDING AUTHORIZATION', PAL.ghost)), transparent: true, side: DoubleSide, fog: false }));
  const mezStencil = new Mesh(new PlaneGeometry(1.05, 0.26), mezStencilMat);
  mezStencil.position.set(35, 1.7, N + 0.24);
  mezStencil.matrixAutoUpdate = false; mezStencil.updateMatrix(); group.add(mezStencil);

  // --- outside split-flap indicator above the cab ----------------------------
  const flapCanvas = mkCanvas(256, 128);
  const flapTex = track(toTexture(flapCanvas[0], { nearest: true }));
  const flapMat = track(new MeshBasicMaterial({ map: flapTex, transparent: true, fog: false, side: DoubleSide }));
  patchMaterial(flapMat, { dimExempt: true });
  const setFloorFlap = (label: string): void => {
    const ctx = flapCanvas[1];
    ctx.fillStyle = css(shade(PAL.carbon, 0.02)); ctx.fillRect(0, 0, 256, 128);
    ctx.fillStyle = css(PAL.amber);
    ctx.font = 'bold 84px monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, 128, 70);
    flapTex.needsUpdate = true;
  };
  setFloorFlap('0');
  const flap = new Mesh(new PlaneGeometry(0.9, 0.45), flapMat);
  flap.position.set(W + 0.45, 3.0, CAB_Z); flap.rotation.y = Math.PI / 2;
  flap.matrixAutoUpdate = false; flap.updateMatrix(); group.add(flap);

  // --- chyron cornice (the Machine's voice travels with you) -----------------
  const chyron = new Chyron(reducedMotion);
  const chyronMat = track(new MeshBasicMaterial({ map: chyron.texture, transparent: true }));
  patchMaterial(chyronMat, { dimExempt: true });
  const cornice = new Mesh(new PlaneGeometry(7, 0.9), chyronMat);
  cornice.position.set(35, 3.7, N + 0.12);
  cornice.matrixAutoUpdate = false; cornice.updateMatrix(); group.add(cornice);

  // --- collision: closed lobby; cab / stair-foot / escalator / mezz are solid -
  const walls: WallSeg[] = [
    { x0: W, z0: N, x1: W, z1: S, h: WALL_H }, // west
    { x0: E, z0: N, x1: E, z1: S, h: WALL_H }, // east
    { x0: W, z0: N, x1: E, z1: N, h: WALL_H }, // north
    { x0: W, z0: S, x1: E, z1: S, h: 2 },      // south
  ];
  const solids: SolidRect[] = [
    { x0: W, z0: CAB_Z - CAB_W / 2 - 0.4, x1: W + 0.6, z1: CAB_Z + CAB_W / 2 + 0.4 }, // cab surround
    { x0: ST_X - 1.1, z0: -2.4, x1: ST_X + 1.1, z1: 1.6 }, // the stair well is a gated barrier (locked landing)
    { x0: ESC_X - 1.0, z0: -4.2, x1: ESC_X + 1.0, z1: 0.0 }, // dead escalator base
    { x0: RIS_X - 0.4, z0: RIS_Z - 1.0, x1: RIS_X + 0.4, z1: RIS_Z + 1.0 }, // riser column
  ];
  const zones: ZoneRect[] = [];
  const bounds: CollisionBounds = { x0: W - 1, z0: N - 1, x1: E + 1, z1: S + 1 };

  const spawns: Record<string, ZoneSpawn> = {
    from_intake: { x: W + 1.6, z: CAB_Z, facing: Math.PI / 2 }, // stepping out of the interior lift door, into the lobby
    from_cab: { x: W + 1.6, z: CAB_Z, facing: Math.PI / 2 },    // stepping out of the cab back into the lobby
  };

  const manifest: ZoneManifest = {
    id: 'vertical_core',
    name: 'THE VERTICAL CIRCULATION CORE',
    residency: 'streamed',
    slabY: 0,
    bounds,
    spawns,
    connections: [
      { toZone: 'floor7', kind: 'walk', triggerId: 'lift_exit', masked: false, overrideable: false },
      { toZone: 'machine_floor', kind: 'elevator', triggerId: 'call_plate', masked: true, overrideable: true },
    ],
    collision: { walls, solids, zones },
    coexistWithResident: false,
  };

  // zone-local hotspots the controller resolves (proximity + its own E)
  const hotspots: ZoneHotspot[] = [
    { id: 'call_plate', x: W + 0.42, z: CAB_Z - 1.6, reach: 1.6, prompt: 'THE CALL PLATE — E TO SUMMON THE CAB' },
    { id: 'dead_plate', x: W + 0.42, z: CAB_Z + 1.6, reach: 1.5, prompt: 'A SECOND CALL PLATE — DARK — E' },
    { id: 'lift_exit', x: W + 1.4, z: CAB_Z, reach: 1.4, prompt: 'BACK TO INTAKE — E' },
    { id: 'stair', x: ST_X, z: 1.4, reach: 1.8, prompt: 'THE STAIR SHAFT — E' },
    { id: 'escalator', x: ESC_X, z: -0.2, reach: 1.8, prompt: 'THE ESCALATOR — E' },
    { id: 'mezzanine', x: 35, z: N + 1.0, reach: 1.6, prompt: 'THE MEZZANINE DOOR — E' },
  ];

  const dispose = (): void => {
    group.traverse((o) => { const m = o as Mesh; if (m.geometry) m.geometry.dispose(); });
    for (const d of disposables) d.dispose();
    chyron.texture.dispose();
    group.clear();
  };

  const tris = bone.tris + leaf.tris + carbon.tris + escSteps.tris + brass.tris +
    stairSteps.tris + pilots.tris + risers.tris + signs.tris + 60;

  return {
    group, manifest, hotspots, chyron, tickRiser, setFloorFlap,
    tris, buildMs: performance.now() - t0, dispose,
  };
}

// ============================================================================
// buildDescentStub — RETIRED by increment M-D. The cheap dead-end landing the
// cab round-tripped to (the M-D placeholder that proved the Q1 phosphor->bone
// fade + full-swap streaming path) is replaced by the real Machine Floor:
// src/client/world/zones/machine.ts (buildMachineFloor). VerticalCoreController
// now streams 'machine_floor' at island OX=320 on the cab-descent route; the
// StubBuild interface is gone and its consumers repoint to MachineFloorBuild.
// ============================================================================
